import { Link } from 'react-router-dom';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import type { Collection } from '@/api/client';

interface DeleteCollectionDialogProps {
    collection: Collection | null;
    open: boolean;
    onClose: () => void;
    afterLeave?: () => void;
    onConfirm: (id: number) => Promise<void>;
    isLoading?: boolean;
}

export function DeleteCollectionDialog({
    collection,
    open,
    onClose,
    afterLeave,
    onConfirm,
    isLoading = false,
}: DeleteCollectionDialogProps) {
    const handleConfirm = async () => {
        if (collection) {
            await onConfirm(collection.id);
        }
    };

    const hasLedgers = (collection?.ledgerCount ?? 0) > 0;
    const ledgersUrl = collection ? `/${collection.type}s?collections=${collection.id}` : '#';
    const itemLabel = collection?.type === 'asset' ? 'asset' : 'debt';
    const itemLabelPlural = collection?.type === 'asset' ? 'assets' : 'debts';

    return (
        <Dialog open={open} onClose={onClose} afterLeave={afterLeave} title="Delete Collection" size="sm">
            <div className="space-y-4">
                {hasLedgers ? (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        <p>
                            The collection{' '}
                            <strong className="font-semibold text-gray-900 dark:text-gray-100">
                                {collection?.label}
                            </strong>{' '}
                            has {collection?.ledgerCount} {collection?.ledgerCount === 1 ? itemLabel : itemLabelPlural} and cannot be deleted.
                        </p>
                        <p className="mt-2">
                            <Link
                                to={ledgersUrl}
                                className="text-accent hover:underline dark:text-primary-light"
                            >
                                View {itemLabelPlural}
                            </Link>{' '}
                            to move or delete them first.
                        </p>
                    </div>
                ) : (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Are you sure you want to delete the collection{' '}
                        <strong className="font-semibold text-gray-900 dark:text-gray-100">
                            {collection?.label}
                        </strong>
                        ? This action cannot be undone.
                    </p>
                )}

                {!hasLedgers && (
                    <div className="flex justify-end gap-3">
                        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleConfirm} isLoading={isLoading}>
                            Delete
                        </Button>
                    </div>
                )}
            </div>
        </Dialog>
    );
}
